// Inventory, recipes and rewards are pure rules. UI and world code must use these transactions.
import { B, BLOCKS } from "./blocks.js";

export const STACK_MAX = 9999;
export const TIERS = {
  wood: {
    name: "Wooden",
    colour: "#bd905b",
    speed: 1,
    power: 1,
    rank: 0,
    learn: 0,
  },
  stone: {
    name: "Stone",
    colour: "#929da9",
    speed: 1.8,
    power: 2,
    rank: 1,
    learn: 12,
  },
  iron: {
    name: "Iron",
    colour: "#dde7ed",
    speed: 3,
    power: 3,
    rank: 2,
    learn: 40,
  },
  diamond: {
    name: "Diamond",
    colour: "#69efeb",
    speed: 5,
    power: 5,
    rank: 3,
    learn: 100,
  },
  ruby: {
    name: "Ruby",
    colour: "#ff668c",
    speed: 8,
    power: 7,
    rank: 4,
    learn: 220,
  },
};
export const ITEMS = {};
for (const [id, name, icon, colour] of [
  ["wood", "Wood", "🪵", "#b88651"],
  ["stone", "Stone", "🪨", "#9aa2a9"],
  ["coal", "Coal", "⚫", "#454858"],
  ["iron", "Iron", "🔩", "#d4b5a0"],
  ["diamond", "Diamond", "💠", "#6fe7eb"],
  ["crystal", "Crystal", "🔮", "#c184ed"],
  ["sand", "Sand", "🏜️", "#dbc479"],
  ["wool", "Wool", "🧶", "#f0e7d8"],
  ["hide", "Hide", "🟤", "#ad744e"],
  ["food", "Food", "🍎", "#ed945d"],
  ["ore", "Raw iron ore", "🪨", "#d4b5a0"],
  ["seeds", "Seeds", "🌱", "#72b752"],
  ["wheat", "Wheat", "🌾", "#e1bd4c"],
  ["egg", "Chicken egg", "🥚", "#fff1d2"],
  ["arrow", "Arrows", "➶", "#dabd8c"],
])
  ITEMS[id] = { id, name, icon, colour, kind: "resource" };
export const BUILD_ITEMS = [
  ["planks", "Oak planks", B.PLANKS],
  ["dirt", "Dirt", B.DIRT],
  ["grass", "Grass", B.GRASS],
  ["cobble", "Cobblestone", B.COBBLE],
  ["dark_planks", "Dark planks", B.DARK_PLANKS],
  ["log", "Log", B.LOG],
  ["stone_brick", "Stone bricks", B.STONE_BRICK],
  ["brick", "Red bricks", B.BRICK],
  ["glass", "Glass", B.GLASS],
  ["leaves", "Leaves", B.LEAVES],
  ["glowstone", "Glowstone", B.GLOWSTONE],
  ["sandstone", "Sandstone", B.SANDSTONE],
  ["snow", "Snow", B.SNOW],
  ["ice", "Ice", B.ICE],
  ["bookshelf", "Bookshelf", B.BOOKSHELF],
  ["lamp", "Lantern", B.LAMP],
  ["blue", "Blue blocks", B.NEON_BLUE],
  ["pink", "Pink blocks", B.NEON_PINK],
  ["white", "White blocks", B.NEON_WHITE],
  ["lime", "Lime blocks", B.NEON_LIME],
  ["gold_block", "Gold blocks", B.GOLD_BLOCK],
  ["diamond_block", "Diamond blocks", B.DIAMOND_BLOCK],
  ["workbench", "Crafting table", B.CRAFTING_TABLE],
  ["bounce", "Bounce pad", B.BOUNCE],
  ["speed", "Speed pad", B.SPEED],
  ...[ ['door','Door'],['chest','Storage chest'],['furnace','Furnace'],['bed','Bed'],['sign_board','Sign'],['slab','Oak slab'],['stairs','Oak stairs'],['fence','Fence'],['ladder','Ladder'],['torch','Torch'],['rail','Rail'],['powered_rail','Powered rail'],['wire','Glow wire'],['switch','Switch'],['circuit_lamp','Magic lamp'],['drawbridge','Drawbridge'],['farm_plot','Garden bed'],['perch','Dragon perch'],['enchant_table','Enchanting table'],['portal_frame','Portal stone'] ].map(([id,n])=>[id,n,B[id.toUpperCase()]]),
];
for (const [id, name, block] of BUILD_ITEMS)
  ITEMS[id] = {
    id,
    name,
    block,
    kind: "block",
    icon:
      id === "workbench"
        ? "🛠️"
        : id === "bounce"
          ? "🦘"
          : id === "speed"
            ? "⚡"
            : "🧱",
    colour: BLOCKS[block].color,
  };
for (const [tier, t] of Object.entries(TIERS))
  for (const [tool, label, icon] of [
    ["axe", "axe", "🪓"],
    ["pick", "pickaxe", "⛏️"],
    ["sword", "sword", "⚔️"],
  ]) {
    const id = `${tool}_${tier}`;
    ITEMS[id] = {
      id,
      name: `${t.name} ${label}`,
      icon,
      colour: t.colour,
      kind: "tool",
      tool,
      tier,
      ...t,
    };
    ITEMS[id].name = `${t.name} ${label}`;
  }
// Existing special swords keep their ownership and gain useful strength too.
export const SWORD_POWER = {
  wood: 1,
  stone: 2,
  iron: 3,
  gold: 3,
  diamond: 5,
  ruby: 7,
  lightning: 5,
  rainbow: 6,
  ice: 5,
  lava: 6,
  glitch: 8,
  crystal: 6, // Crystal Blade (1.7): Locker reward for reaching the top level in 3 topics
};
for (const tier of Object.keys(SWORD_POWER))
  if (!ITEMS[`sword_${tier}`])
    ITEMS[`sword_${tier}`] = {
      id: `sword_${tier}`,
      name: `${tier[0].toUpperCase() + tier.slice(1)} sword`,
      icon: "⚔️",
      colour: "#ffe58e",
      kind: "tool",
      tool: "sword",
      tier,
      rank: 3,
      power: SWORD_POWER[tier],
      speed: 3,
    };
for (const [id,name,icon,cost] of [
  ['saddle','Ember’s saddle','🐉',0],['minecart','Minecart','🛤️',0],['watering_can','Watering can','💧',0],['shears','Shears','✂️',0],['lead','Animal lead','🪢',0],['bow','Bow','🏹',0],['shield','Shield','🛡️',0],['armour_iron','Iron armour','🦺',0],['armour_diamond','Diamond armour','💠',0],['jump_boots','Cloud boots','🥾',0]
]) ITEMS[id]={id,name,icon,kind:'tool',tool:'equipment',colour:id.includes('diamond')?'#67e9ec':'#b7c9dd',speed:1};
export const blockItem = (id) => BUILD_ITEMS.find((x) => x[2] === id)?.[0];
export const amount = (d, id) => d.inventory?.items?.[id] || 0;
export function addItem(d, id, n) {
  if (!ITEMS[id] || !Number.isSafeInteger(n) || n <= 0) return false;
  const cap = ITEMS[id].kind === "tool" ? 1 : STACK_MAX;
  if (amount(d, id) + n > cap) return false;
  d.inventory.items[id] = amount(d, id) + n;
  syncSupplies(d);
  return true;
}
export function syncSupplies(d) {
  d.supplies = BUILD_ITEMS.reduce((n, [id]) => n + amount(d, id), 0);
}
export function ensureInventory(save) {
  const d = save.discovery;
  if (!d.inventory || d.inventory.v !== 1) {
    d.inventory = {
      v: 1,
      items: {
        planks: Math.max(0, Math.min(999, Math.floor(d.supplies || 0))),
        axe_wood: 1,
        pick_wood: 1,
        sword_wood: 1,
      },
      hotbar: [
        `sword_${save.equip.sword}`,
        "axe_wood",
        "pick_wood",
        "planks",
        null,
        null,
        null,
        null,
      ],
      slot: 0,
    };
  }
  const inv = d.inventory;
  inv.items = Object.fromEntries(
    Object.entries(inv.items || {})
      .filter(([id, n]) => ITEMS[id] && Number.isSafeInteger(n) && n > 0)
      .map(([id, n]) => [
        id,
        Math.min(n, ITEMS[id].kind === "tool" ? 1 : STACK_MAX),
      ]),
  );
  for (const tier of save.owned.sword)
    if (ITEMS[`sword_${tier}`]) inv.items[`sword_${tier}`] = 1;
  for (const id of ["axe_wood", "pick_wood", "sword_wood"]) inv.items[id] = 1;
  inv.hotbar = Array.from({ length: 8 }, (_, i) =>
    ITEMS[inv.hotbar?.[i]] ? inv.hotbar[i] : null,
  );
  inv.slot =
    Number.isInteger(inv.slot) && inv.slot >= 0 && inv.slot < 8 ? inv.slot : 0;
  d.removed ||= [];
  d.defeated ||= {};
  d.hearts = Number.isFinite(d.hearts) ? Math.max(1, Math.min(5, d.hearts)) : 5;
  d.crafted ||= {};
  d.gathered ||= {};
  d.projects ||= {};
  d.harvested ||= [];
  d.hunted ||= {};
  d.worldTime = Number.isFinite(d.worldTime) ? Math.max(0, d.worldTime) : 0;
  syncSupplies(d);
  return inv;
}
export function assignSlot(d, id, slot = d.inventory.slot) {
  if (
    !ITEMS[id] ||
    !amount(d, id) ||
    !Number.isInteger(slot) ||
    slot < 0 ||
    slot > 7
  )
    return false;
  // A stack has one hotbar location, while the full quantity always stays in the backpack.
  d.inventory.hotbar = d.inventory.hotbar.map((x) => (x === id ? null : x));
  d.inventory.hotbar[slot] = id;
  d.inventory.slot = slot;
  return true;
}
export const selectedItem = (d) =>
  ITEMS[d.inventory?.hotbar[d.inventory.slot]] || null;
export const RECIPES = [];
const recipe = (id, output, n, cost, extra = {}) =>
  RECIPES.push({
    id,
    output,
    n,
    cost,
    coins: 0,
    gems: 0,
    learn: 0,
    bench: false,
    ...extra,
  });
recipe("dirt", "dirt", 8, { stone: 1 });
recipe("grass", "grass", 8, { dirt: 4, leaves: 1 });
recipe("cobble", "cobble", 8, { stone: 2 });
recipe("planks", "planks", 8, { wood: 1 });
recipe("table", "workbench", 1, { wood: 4, stone: 2 });
for (const [id, n, cost, extra] of [
  ["dark_planks", 8, { wood: 2 }, {}],
  ["log", 4, { wood: 2 }, {}],
  ["stone_brick", 8, { stone: 2 }, {}],
  ["brick", 8, { stone: 2, sand: 1 }, {}],
  ["glass", 8, { sand: 2, coal: 1 }, {}],
  ["leaves", 8, { wood: 1 }, {}],
  ["sandstone", 8, { sand: 2 }, {}],
  ["snow", 8, { stone: 1, wool: 1 }, {}],
  ["ice", 8, { sand: 1, crystal: 1 }, { learn: 12 }],
  ["bookshelf", 2, { wood: 3, wool: 1 }, { learn: 12 }],
  ["lamp", 4, { wood: 1, coal: 1, crystal: 1 }, { coins: 20 }],
  ["glowstone", 8, { stone: 2, crystal: 2 }, { coins: 30, learn: 12 }],
  ["blue", 8, { wool: 2 }, { coins: 10 }],
  ["pink", 8, { wool: 2 }, { coins: 10 }],
  ["white", 8, { wool: 2 }, {}],
  ["lime", 8, { wool: 2 }, { coins: 10 }],
  ["gold_block", 4, { iron: 2, crystal: 1 }, { coins: 80, learn: 40 }],
  ["diamond_block", 4, { diamond: 2 }, { coins: 100, learn: 100 }],
  [
    "bounce",
    2,
    { wool: 4, hide: 2, crystal: 1 },
    { coins: 120, gems: 2, learn: 40 },
  ],
  [
    "speed",
    2,
    { iron: 4, diamond: 1, crystal: 2 },
    { coins: 240, gems: 4, learn: 100 },
  ],
])
  recipe(id, id, n, cost, { bench: true, ...extra });
const tierCosts = {
  stone: { wood: 4, stone: 8 },
  iron: { wood: 6, iron: 8, coal: 4 },
  diamond: { wood: 8, iron: 8, diamond: 6 },
  ruby: { iron: 12, diamond: 10, crystal: 18 },
};
const fees = {
  stone: [90, 0],
  iron: [350, 4],
  diamond: [1200, 15],
  ruby: [2800, 35],
};
for (const tier of ["stone", "iron", "diamond", "ruby"])
  for (const tool of ["axe", "pick", "sword"]) {
    const prev = Object.keys(TIERS)[TIERS[tier].rank - 1];
    recipe(`${tool}_${tier}`, `${tool}_${tier}`, 1, tierCosts[tier], {
      bench: true,
      learn: TIERS[tier].learn,
      coins: fees[tier][0],
      gems: fees[tier][1],
      requires: `${tool}_${prev}`,
    });
  }
// Distinct useful unlocks; essentials stay affordable and work in both modes where relevant.
for (const [id,n,cost,learn=0,coins=0,gems=0] of [
 ['door',2,{wood:2}],['chest',1,{wood:4}],['furnace',1,{stone:8}],['bed',1,{wood:3,wool:3}],['sign_board',2,{wood:1}],['slab',8,{wood:1}],['stairs',4,{wood:2}],['fence',4,{wood:2}],['ladder',4,{wood:2}],['torch',8,{wood:1,coal:1}],
 ['rail',8,{iron:2,wood:2},12],['powered_rail',4,{iron:3,crystal:1},24,40,1],['wire',8,{crystal:1,iron:1},12],['switch',1,{wood:1,stone:1}],['circuit_lamp',2,{glass:2,crystal:1},12],['drawbridge',2,{wood:3,iron:1},12],['farm_plot',4,{dirt:2}],['perch',1,{wood:6,stone:4,crystal:2},18,60,2],['enchant_table',1,{stone:6,crystal:4,bookshelf:1},24,120,3],['portal_frame',4,{stone:4,crystal:2},40,80,2],
 ['saddle',1,{hide:4,wool:4,crystal:4},24,150,4],['minecart',1,{iron:6,wood:4},12,80],['watering_can',1,{wood:2}],['shears',1,{iron:2}],['lead',1,{wool:3}],['bow',1,{wood:5,wool:3},12,60],['arrow',12,{wood:1,stone:1}],['shield',1,{wood:4,iron:2},12,60],['armour_iron',1,{iron:12,hide:4},40,250,4],['armour_diamond',1,{diamond:10,iron:12},100,800,12],['jump_boots',1,{wool:6,crystal:6},24,160,4],['seeds',4,{leaves:1}],['food_bread',2,{wheat:3}]
]) recipe(id,id==='food_bread'?'food':id,n,cost,{bench:!['farm_plot','seeds','torch','watering_can','arrow','food_bread'].includes(id),learn,coins,gems});
for(const [id,blueprint]of [['powered_rail','mine'],['portal_frame','sky'],['enchant_table','temple']])RECIPES.find(r=>r.id===id).blueprint=blueprint;
export const SHOP = [
  { id: "timber", name: "Timber bundle", output: "wood", n: 8, coins: 60 },
  { id: "stone", name: "Stone bundle", output: "stone", n: 12, coins: 70 },
  { id: "sand", name: "Sand bundle", output: "sand", n: 12, coins: 65 },
  { id: "wool", name: "Wool bundle", output: "wool", n: 8, coins: 90 },
  { id: "coal", name: "Coal bundle", output: "coal", n: 8, coins: 90 },
  {
    id: "iron",
    name: "Iron bundle",
    output: "iron",
    n: 6,
    coins: 160,
    learn: 12,
  },
  {
    id: "crystal",
    name: "Crystal bundle",
    output: "crystal",
    n: 4,
    coins: 160,
    gems: 2,
    learn: 40,
  },
  {
    id: "diamond",
    name: "Diamond bundle",
    output: "diamond",
    n: 3,
    coins: 300,
    gems: 3,
    learn: 100,
  },
];
for (const bundle of SHOP) bundle.bench = true;
export function recipeStatus(save, r, nearBench = false) {
  const d = save.discovery,
    missing = [];
  if (!r) return { ok: false, missing: ["Unknown recipe"] };
  if (ITEMS[r.output]?.kind === "tool" && amount(d, r.output))
    missing.push("Already owned");
  if ((save.stats.correct || 0) < (r.learn || 0) && !(r.blueprint && d.expansion?.adventures?.[r.blueprint]))
    missing.push(
      `Learn ${r.learn - save.stats.correct} more answers (${save.stats.correct}/${r.learn})`,
    );
  if (r.bench && !nearBench) missing.push("Go to a crafting table");
  if (r.requires && !amount(d, r.requires))
    missing.push(`Make ${ITEMS[r.requires].name} first`);
  for (const [id, n] of Object.entries(r.cost || {}))
    if (amount(d, id) < n)
      missing.push(`${n - amount(d, id)} more ${ITEMS[id].name.toLowerCase()}`);
  if (save.coins < (r.coins || 0))
    missing.push(`${r.coins - save.coins} more coins`);
  if (save.gems < (r.gems || 0))
    missing.push(`${r.gems - save.gems} more gems`);
  if (
    amount(d, r.output) + r.n >
    (ITEMS[r.output]?.kind === "tool" ? 1 : STACK_MAX)
  )
    missing.push("Stack is full");
  return { ok: missing.length === 0, missing };
}
export function craft(save, id, nearBench = false, shop = false) {
  const r = (shop ? SHOP : RECIPES).find((r) => r.id === id),
    status = recipeStatus(save, r, nearBench);
  if (!status.ok) return status;
  const d = save.discovery;
  for (const [item, n] of Object.entries(r.cost || {}))
    d.inventory.items[item] -= n;
  save.coins -= r.coins || 0;
  save.gems -= r.gems || 0;
  addItem(d, r.output, r.n);
  if (!shop) d.crafted[r.output] = (d.crafted[r.output] || 0) + r.n;
  if (r.output.startsWith("sword_")) {
    const tier = ITEMS[r.output].tier;
    if (!save.owned.sword.includes(tier)) save.owned.sword.push(tier);
    save.equip.sword = tier;
  }
  const free = d.inventory.hotbar.indexOf(null);
  if (free >= 0 && !d.inventory.hotbar.includes(r.output))
    d.inventory.hotbar[free] = r.output;
  return { ok: true, item: r.output, n: r.n };
}
export const HARVEST = {};
function harvest(blocks, item, n, time, tool, rank = 0) {
  for (const id of blocks) HARVEST[id] = { item, n, time, tool, rank };
}
harvest([B.DIRT, B.GRASS, B.SNOW_GRASS, B.PATH], "dirt", 1, 1.2, "pick");
harvest([B.PLANKS, B.DARK_PLANKS, B.BOOKSHELF], "wood", 2, 2.5, "axe");
harvest([B.STONE_BRICK, B.BRICK, B.BASALT, B.MARBLE], "stone", 2, 4.5, "pick");
harvest([B.GLASS], "glass", 1, 1, "pick");
harvest([B.GLOWSTONE, B.LAMP], "crystal", 1, 5, "pick", 1);
harvest([B.GOLD_BLOCK], "iron", 2, 6, "pick", 1);
harvest([B.SNOW, B.ICE], "snow", 1, 1.8, "pick");
harvest([B.LOG, B.BIRCH_LOG, B.PINE_LOG], "wood", 3, 3, "axe");
harvest(
  [B.LEAVES, B.BIRCH_LEAVES, B.PINE_LEAVES, B.SNOW_PINE_LEAVES],
  "leaves",
  1,
  0.9,
  "axe",
);
harvest([B.STONE, B.COBBLE], "stone", 3, 4, "pick");
harvest([B.COAL_ORE], "coal", 2, 5, "pick");
harvest([B.IRON_ORE], "ore", 2, 6, "pick", 1);
harvest([B.DIAMOND_ORE], "diamond", 1, 8, "pick", 2);
harvest([B.CRYSTAL, B.RUBY_ORE], "crystal", 2, 7, "pick", 2);
harvest([B.SAND, B.SANDSTONE], "sand", 3, 2.4, "pick");
export function harvestSeconds(block, item, placed = false) {
  const h =
    HARVEST[block] ||
    (placed
      ? {
          time: BLOCKS[block]?.step === "metal" ? 7 : 2.5,
          tool: BLOCKS[block]?.step === "wood" ? "axe" : "pick",
          rank: 0,
        }
      : null);
  if (!h) return Infinity;
  const t = ITEMS[item];
  if (
    !placed &&
    h.rank > 0 &&
    ((t?.tool !== h.tool && t?.tool !== "sword") || t.rank < h.rank)
  )
    return Infinity;
  return (
    h.time /
    (t?.tool === h.tool
      ? t.speed
      : t?.tool === "sword"
        ? Math.max(0.6, (t.speed || 1) * 0.32)
        : 0.45)
  );
}
export const PROJECTS = [
  {
    id: "shelter",
    name: "A home for Scout",
    text: "Place 12 oak planks on your plot or in the wild.",
    blocks: { planks: 12 },
    learn: 3,
    coins: 60,
    gems: 2,
  },
  {
    id: "workshop",
    name: "Your own workshop",
    text: "Craft and place your own crafting table.",
    blocks: { workbench: 1 },
    learn: 6,
    coins: 90,
    gems: 3,
  },
  {
    id: "windows",
    name: "A house with windows",
    text: "Build with 12 stone bricks and 4 glass blocks.",
    blocks: { stone_brick: 12, glass: 4 },
    learn: 12,
    coins: 150,
    gems: 4,
  },
  {
    id: "colour",
    name: "Colourful castle",
    text: "Place 8 blue blocks and 8 pink blocks.",
    blocks: { blue: 8, pink: 8 },
    learn: 24,
    coins: 180,
    gems: 5,
  },
  {
    id: "glow",
    name: "Light up the night",
    text: "Place 8 glowstone blocks and 4 lanterns.",
    blocks: { glowstone: 8, lamp: 4 },
    learn: 40,
    coins: 260,
    gems: 6,
  },
  {
    id: "park",
    name: "{name}’s jump park",
    text: "Build a course with 4 bounce pads and 2 speed pads.",
    blocks: { bounce: 4, speed: 2 },
    learn: 100,
    coins: 500,
    gems: 10,
  },
];
export function projectStatus(save, p) {
  const counts = {};
  for (const b of save.discovery.blocks) {
    const id = blockItem(b[3]);
    counts[id] = (counts[id] || 0) + 1;
  }
  return {
    ready:
      !save.discovery.projects[p.id] &&
      save.stats.correct >= p.learn &&
      Object.entries(p.blocks).every(([id, n]) => (counts[id] || 0) >= n),
    counts,
  };
}
export function claimProject(save, id) {
  const p = PROJECTS.find((p) => p.id === id);
  if (!p || !projectStatus(save, p).ready) return false;
  save.discovery.projects[id] = true;
  save.coins += p.coins;
  save.gems += p.gems;
  save.stats.coinsTotal += p.coins;
  save.stats.gemsTotal += p.gems;
  return true;
}

export function cleanHarvested(d) {
  const seen = new Set();
  d.harvested = (Array.isArray(d.harvested) ? d.harvested : [])
    .filter((b) => {
      if (
        !Array.isArray(b) ||
        b.length !== 5 ||
        !b.slice(0, 4).every(Number.isInteger) ||
        Math.abs(b[0]) > 1e7 ||
        Math.abs(b[2]) > 1e7 ||
        b[1] < 38 ||
        b[1] > 96 ||
        !HARVEST[b[3]] ||
        !Number.isFinite(b[4]) ||
        b[4] <= d.worldTime ||
        b[4] > d.worldTime + 300
      )
        return false;
      const k = b.slice(0, 3).join(",");
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(-1024);
  d.removed = (Array.isArray(d.removed) ? d.removed : [])
    .filter(
      (b) =>
        Array.isArray(b) &&
        b.length === 4 &&
        b.every(Number.isInteger) &&
        Math.abs(b[0]) <= 1e7 &&
        Math.abs(b[2]) <= 1e7 &&
        b[1] >= 9 &&
        b[1] <= 96 &&
        HARVEST[b[3]],
    )
    .slice(-2048);
  d.defeated = Object.fromEntries(
    Object.entries(d.defeated || {})
      .filter(
        ([id, t]) =>
          /^-?\d+,-?\d+$/.test(id) &&
          Number.isFinite(t) &&
          t > d.worldTime &&
          t <= d.worldTime + 180,
      )
      .slice(-128),
  );
  d.hunted = Object.fromEntries(
    Object.entries(d.hunted || {})
      .filter(
        ([id, t]) =>
          /^-?\d+,-?\d+:\d$/.test(id) &&
          Number.isFinite(t) &&
          t > d.worldTime &&
          t <= d.worldTime + 180,
      )
      .slice(-128),
  );
}
